import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import document1 from "../../../../assets/document1.svg";
import CustomButton from "../../../../components/CustomButton";
import { toast } from "react-hot-toast";
import maparrow from "../../../../assets/maparrow.svg";
import LocationPermissionModal from "../../../../components/PermissionModal";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const formatUTCDateForInput = (utcValue) => {
  const formatted = convertUTCToLocalDateTime(utcValue, {
    locale: "sv-SE",
    formatOptions: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    },
  });
  return formatted === "—" ? "" : formatted;
};

const formatUTCTimeForInput = (utcValue) => {
  const formatted = convertUTCToLocalDateTime(utcValue, {
    locale: "en-GB",
    formatOptions: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
  });
  return formatted === "—" ? "" : formatted.replace(/\u202f|\u00a0/g, " ").trim();
};

const EditTask = ({ open, onClose, taskToEdit, onTaskUpdated }) => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

  const [hasCheckedPermission, setHasCheckedPermission] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Add helper function to check internet
  const isOnline = () => navigator.onLine;

  // Enable the close ("X") button on the native Street View panorama that
  // opens when the map's built-in pegman is dragged onto the map, so users
  // can get back to the map view (it's off by default in the JS API), and
  // force a resize once visible since the panorama can otherwise render
  // black inside a React-managed container.
  const setupStreetView = (map) => {
    const panorama = map.getStreetView();
    panorama.setOptions({
      enableCloseButton: true,
      // Restrict the pegman to official outdoor Street View coverage only.
      // Without this it can snap to low-quality/broken user- or business-
      // contributed 360 photos (indoor photospheres), which sometimes render
      // solid black.
      source: window.google.maps.StreetViewSource.OUTDOOR,
    });
    const triggerResize = () => {
      requestAnimationFrame(() => {
        window.google.maps.event.trigger(panorama, "resize");
      });
      setTimeout(() => {
        window.google.maps.event.trigger(panorama, "resize");
      }, 300);
    };

    panorama.addListener("visible_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
    panorama.addListener("pano_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
  };

  const [loading, setLoading] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);

  const priorityOptions = [
    { label: t("priorityOptions.low"), value: "low" },
    { label: t("priorityOptions.medium"), value: "medium" },
    { label: t("priorityOptions.high"), value: "high" },
    { label: t("priorityOptions.urgent"), value: "urgent" },
  ];

  const [workers, setWorkers] = useState([]);

  const getInitialFormValues = () => ({
    id: null,
    name: "",
    workers: [],
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    description: "",
    priority: "",
    faceRestriction: true,
    locationRestriction: true,
    location: { lat: 40.4168, lng: -3.7038, address: "" },
    radius: 200,
    attachments: [],
  });

  const formik = useFormik({
    initialValues: getInitialFormValues(),
    validate: (values) => {
      const errors = {};

      if (!values.name || values.name.trim() === "") {
        errors.name =
          t("taskAddModal.validation.taskNameRequired") ||
          "Task name is required";
      }

      if (!values.priority || values.priority.trim() === "") {
        errors.priority =
          t("taskAddModal.validation.priorityRequired") ||
          "Priority is required";
      }

      if (!Array.isArray(values.workers) || values.workers.length === 0) {
        errors.workers =
          t("taskAddModal.validation.workersRequired") ||
          "At least one worker must be assigned";
      }

      if (!values.startDate || values.startDate.trim() === "") {
        errors.startDate =
          t("taskAddModal.validation.startDateRequired") ||
          "Start date is required";
      }

      if (!values.endDate || values.endDate.trim() === "") {
        errors.endDate =
          t("taskAddModal.validation.endDateRequired") ||
          "End date is required";
      }

      if (!values.startTime || values.startTime.trim() === "") {
        errors.startTime =
          t("taskAddModal.validation.startTimeRequired") ||
          "Start time is required";
      }

      if (!values.endTime || values.endTime.trim() === "") {
        errors.endTime =
          t("taskAddModal.validation.endTimeRequired") ||
          "End time is required";
      }

      if (
        values.startDate &&
        values.endDate &&
        values.startTime &&
        values.endTime
      ) {
        const start = new Date(`${values.startDate}T${values.startTime}`);
        const end = new Date(`${values.endDate}T${values.endTime}`);

        if (!isNaN(start) && !isNaN(end)) {
          if (end <= start) {
            errors.endTime =
              t("taskAddModal.validation.endTimeAfterStart") ||
              "End date & time must be after start date & time";
          }
        }
      }

      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      console.log("==================================");
      console.log("🚀 TASK UPDATE SUBMISSION STARTED");
      console.log("==================================");
      console.log("📋 Form values:", values);

      setLoading(true);

      const startAtUTC = convertLocalDateTimeToUTC(
        `${values.startDate}T${values.startTime}:00`
      );
      const endAtUTC = convertLocalDateTimeToUTC(
        `${values.endDate}T${values.endTime}:00`
      );

      if (!startAtUTC || !endAtUTC) {
        toast.error(
          t("taskAddModal.validation.invalidDateTime") ||
            "Please select valid start and end date/time"
        );
        setLoading(false);
        return;
      }

      const body = {
        title: values.name,
        description: values.description,
        priority: values.priority,
        start_at: startAtUTC,
        end_at: endAtUTC,
        location: {
          address: values.location.address || "",
          lat: values.location.lat,
          lng: values.location.lng,
          radius_m: Number(values.radius),
        },
        assignees: values.workers,
        attachments: values.attachments,
        face_required: true,
        location_required: true,
        evidence_required: true,
      };

      console.log("📤 Sending payload:", JSON.stringify(body, null, 2));

      try {
        const res = await fetch(
          `${BASE_URL}/task-management/admin/tasks/${values.id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
            body: JSON.stringify(body),
          }
        );

        const data = await res.json();
        console.log("📥 Response:", data);

        if (!data.error) {
          console.log("✅ Task updated successfully");
          toast.success("Task updated successfully");
          if (onTaskUpdated) onTaskUpdated();
          onClose();
        } else {
          console.log("❌ API Error:", data.message);
          toast.error(data.message || "Failed to update task");
        }
      } catch (err) {
        console.error("❌ Update failed:", err);
        toast.error("Network error while updating task");
      } finally {
        setLoading(false);
        console.log("==================================");
        console.log("🏁 SUBMISSION ENDED");
        console.log("==================================");
      }
    },
  });

  // Prefill when editing
  useEffect(() => {
    console.log("📩 taskToEdit received in drawer:", taskToEdit);
  }, [taskToEdit]);

  useEffect(() => {
    if (taskToEdit && open) {
      console.log("📝 Editing task ID:", taskToEdit.id);
      console.log("Task Assignments:", taskToEdit.assignments);
      
      const values = {
        id: taskToEdit.id,
        name: taskToEdit.title || "",
        description: taskToEdit.description || "",
        priority: taskToEdit.priority || "",
        workers: taskToEdit.assignments?.map((a) => a.worker_id) || [],
        startDate: formatUTCDateForInput(taskToEdit.start_at),
        startTime: formatUTCTimeForInput(taskToEdit.start_at),
        endDate: formatUTCDateForInput(taskToEdit.end_at),
        endTime: formatUTCTimeForInput(taskToEdit.end_at),
        location: {
          lat: Number(taskToEdit.location_lat) || 40.4168,
          lng: Number(taskToEdit.location_lng) || -3.7038,
          address: taskToEdit.location_address || "Fetching address...",
        },
        radius: taskToEdit.location_radius_m || 200,
        faceRestriction: taskToEdit.face_required ?? true,
        locationRestriction: taskToEdit.location_required ?? true,
        attachments: taskToEdit.attachments || [],
      };
      
      formik.setValues(values);
      formik.resetForm({ values, touched: {}, errors: {} });

      // ✅ Fetch fresh address for the existing coordinates
      if (taskToEdit.location_lat && taskToEdit.location_lng) {
        console.log("🔄 Fetching fresh address for existing coordinates");
        fetchAddress(Number(taskToEdit.location_lat), Number(taskToEdit.location_lng));
      }

      // Reset permission check when drawer opens
      setHasCheckedPermission(false);
    } else if (!open) {
      formik.resetForm();
      setHasCheckedPermission(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskToEdit, open]);

  const [uploadedFile, setUploadedFile] = useState(null);

  // Fetch workers
  const fetchWorkers = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/public/workers?no_pagination=true&company_id=${companyId}&status=active`,
        {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch workers");
      const data = await res.json();
      setWorkers(
        (data?.data?.records || []).map((w) => ({
          label: `${w.first_name} ${w.last_name} (${w.email})`,
          value: w.id,
          email: w.email,
          fullLabel: `${w.first_name} ${w.last_name}\n${w.email}`,
        }))
      );
    } catch (err) {
      console.error("Error fetching workers:", err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchWorkers();
    }
  }, [open]);

  // Check permission on drawer open (only once)
  useEffect(() => {
    if (open && !hasCheckedPermission && !taskToEdit) {
      setHasCheckedPermission(true);
      console.log("🚀 EditDrawer opened - Checking permission...");

      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'denied') {
            console.log("❌ Permission denied");
            setShowPermissionModal(true);
          } else if (result.state === 'granted') {
            console.log("✅ Permission granted");
            // Don't auto-fetch location when editing
          } else {
            console.log("❓ Permission prompt");
          }
        });
      }
    }

    if (!open) {
      setHasCheckedPermission(false);
    }
  }, [open, hasCheckedPermission, taskToEdit]);

  const fetchAddress = async (lat, lng) => {
    if (!window.google) {
      console.log("⚠️ Google Maps not loaded");
      return "";
    }

    if (!isOnline()) {
      console.error("❌ No internet connection");
      toast.error(t("locationInternetError") || "No internet connection");
      formik.setFieldValue("location", {
        lat: lat,
        lng: lng,
        address: "No internet connection",
      });
      return "";
    }

    console.log("🗺️ Fetching address for:", { lat, lng });

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        console.log("🗺️ Geocoding status:", status);
        console.log("🗺️ Geocoding results:", results);

        if (status === "OK" && results[0]) {
          const address = results[0].formatted_address;
          console.log("✅ Address fetched:", address);
          
          formik.setFieldValue("location", {
            lat: lat,
            lng: lng,
            address: address,
          });
          
          resolve(address);
        } else {
          if (!isOnline()) {
            console.error("❌ No internet connection");
            formik.setFieldValue("location", {
              lat: lat,
              lng: lng,
              address: "No internet connection",
            });
          } else {
            console.error("❌ Geocoding failed:", status);
            formik.setFieldValue("location", {
              lat: lat,
              lng: lng,
              address: "Address not found",
            });
          }
          resolve("");
        }
      });
    });
  };

  // Search suggestions
  useEffect(() => {
    if (!searchQuery.trim() || !window.google) {
      setSuggestions([]);
      return;
    }

    if (!isOnline()) {
      console.log("⚠️ No internet - skipping search");
      setSuggestions([]);
      return;
    }

    console.log("🔍 Searching for:", searchQuery);

    const service = new window.google.maps.places.AutocompleteService();

    const request = {
      input: searchQuery,
      types: ["geocode", "establishment"],
    };

    service.getPlacePredictions(request, (predictions, status) => {
      console.log("🔍 Predictions status:", status);

      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictions
      ) {
        setSuggestions(predictions);
      } else {
        setSuggestions([]);
      }
    });
  }, [searchQuery]);

  const handleSearchSelect = async (suggestion) => {
    if (!window.google) return;

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    console.log("📍 Selected suggestion:", suggestion);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: suggestion.place_id }, async (results, status) => {
      console.log("📍 Place geocoding status:", status);

      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const newLocation = {
          lat: location.lat(),
          lng: location.lng(),
          address: results[0].formatted_address,
        };

        console.log("✅ Selected location:", newLocation);

        formik.setFieldValue("location", newLocation);
        formik.setFieldTouched("location", true);
        setSearchQuery("");
        setSuggestions([]);

        if (mapRef) {
          mapRef.panTo(newLocation);
          mapRef.setZoom(15);
        }
      }
    });
  };

  const handleMapClick = async (e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    console.log("🖱️ Map clicked at:", coords);

    const address = await fetchAddress(coords.lat, coords.lng);
    formik.setFieldTouched("location", true);
  };

  const handleMarkerDragEnd = async (e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    console.log("🔄 Marker dragged to:", coords);

    const address = await fetchAddress(coords.lat, coords.lng);
    formik.setFieldTouched("location", true);
  };

  const handleGetCurrentLocation = () => {
    console.log("📍 Requesting current location...");

    if (!navigator.geolocation) {
      console.error("❌ Geolocation not supported");
      toast.error(t("geolocationNotSupported") || "Geolocation not supported");
      setShowPermissionModal(true);
      return;
    }

    if (!isOnline()) {
      console.error("❌ No internet connection");
      toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
      setShowPermissionModal(true);
      return;
    }

    getPositionWithFallback(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        console.log("✅ Current location:", coords);
        
        fetchAddress(coords.lat, coords.lng);
        setShowPermissionModal(false);

        if (mapRef) {
          mapRef.panTo(coords);
          mapRef.setZoom(15);
        }
      },
      (error) => {
        console.error("❌ Location error:", error);

        if (error.code === error.PERMISSION_DENIED) {
          console.log("❌ Permission denied by user");
          setShowPermissionModal(true);
        } else if (error.code === error.TIMEOUT) {
          toast.error(t("locationTimeout") || "Location request timed out");
        } else {
          toast.error(t("unableToFetchLocation") || "Unable to fetch location");
        }
      }
    );
  };

  // Online/Offline listeners

  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      if (showPermissionModal) {
        setShowPermissionModal(false);
        handleGetCurrentLocation();
      }
    },
    onDenied: () => setShowPermissionModal(true),
  });
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      if (formik.values.location?.lat && formik.values.location?.lng) {
        fetchAddress(formik.values.location.lat, formik.values.location.lng);
      }
    };

    const handleOffline = () => {
      toast.error(t("internetConnectionLost") || "Internet connection lost. Please check your network.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [formik.values.location]);

  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("pdf", file);

    try {
      const res = await fetch(`${BASE_URL}/upload/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${decryptedToken}` },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        const fileUrl = data?.data?.url;

        setUploadedFile({
          name: file.name,
          url: fileUrl || null,
          size: file.size,
          type: file.type,
        });

        formik.setFieldValue("attachments", [fileUrl]);
      }
    } catch (err) {
      console.error("❌ Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const startRef = useRef(null);
  const endRef = useRef(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 500, maxWidth: "100%", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 1.3,
          borderBottom: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
            {t("taskAddModal.updateTask")}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
        {/* Task Name */}
        <Box display="flex" gap={2} mb={1}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <label
              className="field_label"
              style={{ marginBottom: "3px" }}
            >
              {t("taskAddModal.taskName")}
              <span className="red_asterisk">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="project_input"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                height: "40px",
                border:
                  formik.touched.name && formik.errors.name
                    ? "2px solid #d32f2f"
                    : "2px solid #091E4224",
              }}
            />
            {formik.touched.name && formik.errors.name && (
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {formik.errors.name}
              </span>
            )}
          </Box>
        </Box>

        {/* Priority */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <MultiSelectDropdown
            isSingleSelect={true}
            label={
              <>
                {t("taskAddModal.priority")} <span className="red_asterisk">*</span>
              </>
            }
            name="priority"
            values={formik.values.priority ? [formik.values.priority] : []}
            options={priorityOptions}
            onChange={(e) => {
              formik.setFieldValue("priority", e.target.value[0] || "");
              formik.setFieldTouched("priority", true);
            }}
          />
          {formik.touched.priority && formik.errors.priority && (
            <span
              style={{
                color: "#d32f2f",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {formik.errors.priority}
            </span>
          )}
        </Box>

        {/* Workers */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", mb: 1 }}>
          <MultiSelectDropdown
            isSingleSelect={true}
            label={
              <>
                {t("taskAddModal.assigningWorkers")}
                <span className="red_asterisk">*</span>
              </>
            }
            name="workers"
            values={formik.values.workers}
            enableSearch={true}
            options={workers}
            onChange={(e) => {
              formik.setFieldValue("workers", e.target.value);
              formik.setFieldTouched("workers", true);
            }}
          />
          {formik.touched.workers && formik.errors.workers && (
            <span
              style={{
                color: "#d32f2f",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {formik.errors.workers}
            </span>
          )}
        </Box>

        {/* Dates */}
        <Box display="flex" gap={2} mb={2}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <label className="field_label">
              {t("taskAddModal.startingDate")}
              <span className="red_asterisk">*</span>
            </label>
            <input
              ref={startRef}
              onClick={() => startRef.current?.showPicker()}
              type="date"
              name="startDate"
              className="project_input"
              value={formik.values.startDate || ""}
              onChange={(e) => {
                let val = e.target.value;
                const parts = val.split("-");
                if (parts[0] && parts[0].length > 4) {
                  parts[0] = parts[0].slice(0, 4);
                  val = parts.join("-");
                }
                const validFormat = /^\d{0,4}(-\d{0,2}){0,2}$/;
                if (validFormat.test(val)) {
                  formik.setFieldValue("startDate", val);
                }
              }}
              onBlur={formik.handleBlur}
              style={{
                height: "40px",
                border:
                  formik.touched.startDate && formik.errors.startDate
                    ? "2px solid #d32f2f"
                    : "2px solid #091E4224",
              }}
            />
            {formik.touched.startDate && formik.errors.startDate && (
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {formik.errors.startDate}
              </span>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <label className="field_label">
              {t("taskAddModal.endingDate")}
              <span className="red_asterisk">*</span>
            </label>
            <input
              ref={endRef}
              onClick={() => endRef.current?.showPicker()}
              type="date"
              name="endDate"
              className="project_input"
              value={formik.values.endDate || ""}
              onChange={(e) => {
                let val = e.target.value;
                const parts = val.split("-");
                if (parts[0] && parts[0].length > 4) {
                  parts[0] = parts[0].slice(0, 4);
                  val = parts.join("-");
                }
                const validFormat = /^\d{0,4}(-\d{0,2}){0,2}$/;
                if (validFormat.test(val)) {
                  formik.setFieldValue("endDate", val);
                }
              }}
              onBlur={formik.handleBlur}
              style={{
                height: "40px",
                border:
                  formik.touched.endDate && formik.errors.endDate
                    ? "2px solid #d32f2f"
                    : "2px solid #091E4224",
              }}
            />
            {formik.touched.endDate && formik.errors.endDate && (
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {formik.errors.endDate}
              </span>
            )}
          </Box>
        </Box>

        {/* Times */}
        <Box display="flex" gap={2} mb={2}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <label className="field_label">
              {t("taskAddModal.startingTime")}
              <span className="red_asterisk">*</span>
            </label>
            <input
              type="time"
              name="startTime"
              className="project_input"
              value={formik.values.startTime || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                height: "40px",
                border:
                  formik.touched.startTime && formik.errors.startTime
                    ? "2px solid #d32f2f"
                    : "2px solid #091E4224",
              }}
            />
            {formik.touched.startTime && formik.errors.startTime && (
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {formik.errors.startTime}
              </span>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <label className="field_label">
              {t("taskAddModal.endingTime")}
              <span className="red_asterisk">*</span>
            </label>
            <input
              type="time"
              name="endTime"
              className="project_input"
              value={formik.values.endTime || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{
                height: "40px",
                border:
                  formik.touched.endTime && formik.errors.endTime
                    ? "2px solid #d32f2f"
                    : "2px solid #091E4224",
              }}
            />
            {formik.touched.endTime && formik.errors.endTime && (
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {formik.errors.endTime}
              </span>
            )}
          </Box>
        </Box>

        {/* Description */}
        <Box mb={2} display="flex" flexDirection="column">
          <label className="field_label">
            {t("taskAddModal.description")}
          </label>
          <textarea
            name="description"
            rows={4}
            className="project_input"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Box>

        {/* Supporting Document */}
        <Box mb={2} display="flex" flexDirection="column">
          <label className="field_label">
            {t("taskAddModal.supportingDocument")}
          </label>

          {!uploadedFile ? (
            <Box
              sx={{
                border: "2px dashed #D0D5DD",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                cursor: "pointer",
                py: 6,
              }}
              onClick={() => document.getElementById("fileInput").click()}
            >
              {uploading ? (
                <CircularProgress size={20} />
              ) : (
                <span>{t("taskAddModal.dragFiles")}</span>
              )}
              <input
                id="fileInput"
                type="file"
                accept="application/pdf,image/*"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            </Box>
          ) : (
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                position: "relative",
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setUploadedFile(null);
                  formik.setFieldValue("attachments", []);
                }}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  backgroundColor: "#fff",
                  border: "1px solid #E0E0E0",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <img
                  src={profilecross}
                  alt="remove"
                  style={{ width: 16, height: 16 }}
                />
              </IconButton>

              <Box
                component="img"
                src={document1}
                alt="file"
                sx={{ width: 45, height: 45, objectFit: "contain" }}
              />

              <CustomText
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#172B4D",
                  wordBreak: "break-word",
                }}
              >
                {uploadedFile.name}
              </CustomText>

              <CustomText
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: "#6B778C",
                }}
              >
                {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
              </CustomText>
            </Box>
          )}
        </Box>

        {/* Face Scan Restriction — hidden; always sent as true in API */}
        {/*
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <label className="field_label">
            {t("taskAddModal.faceRestriction")}
          </label>
          <Switch
            checked={formik.values.faceRestriction}
            onChange={(e) =>
              formik.setFieldValue("faceRestriction", e.target.checked)
            }
          />
        </Box>
        */}

        {/* Location Restriction — hidden; always sent as true in API */}
        {/*
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <label className="field_label">
            {t("taskAddModal.locationRestriction")}
          </label>
          <Switch
            checked={formik.values.locationRestriction}
            onChange={(e) =>
              formik.setFieldValue("locationRestriction", e.target.checked)
            }
          />
        </Box>
        */}

        {/* Radius input */}
        <Box mb={2} display="flex" flexDirection="column">
          <label className="field_label">
            {t("taskAddModal.radius")}
          </label>
          <input
            type="number"
            name="radius"
            className="project_input"
            value={formik.values.radius}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ height: "40px" }}
          />
        </Box>

        {/* Map */}
        <Box mb={3} display="flex" flexDirection="column">
            <label className="label_field">
              {t("taskAddModal.pickLocation")}
            </label>

            {/* Search Bar */}
            <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
              <Box sx={{ flex: 1, position: "relative" }}>
                <input
                  type="text"
                  placeholder="Search location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "14px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                />

                {/* Suggestions dropdown */}
                {suggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 10000,
                      mt: 0.5,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    }}
                  >
                    <List>
                      {suggestions.map((sug) => (
                        <ListItem
                          key={sug.place_id}
                          button
                          onClick={() => handleSearchSelect(sug)}
                          sx={{
                            "&:hover": { backgroundColor: "#f0f0f0" },
                            cursor: "pointer",
                          }}
                        >
                          <ListItemText
                            primary={sug.structured_formatting.main_text}
                            secondary={
                              sug.structured_formatting.secondary_text
                            }
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontFamily: "Poppins, sans-serif",
                            }}
                            secondaryTypographyProps={{
                              fontSize: "12px",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              <IconButton
                onClick={() => {
                  const newState = !searchOpen;
                  setSearchOpen(newState);
                  if (!newState) {
                    setSearchQuery("");
                    setSuggestions([]);
                  }
                }}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#115293" },
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Map container */}
            <Box sx={{ position: "relative" }}>
              <Box sx={{ height: 300, borderRadius: "8px", overflow: "hidden" }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={formik.values.location}
                  zoom={15}
                  onClick={handleMapClick}
                  onLoad={(map) => {
                    console.log("🗺️ Preview map loaded");
                    setMapRef(map);
                    setupStreetView(map);
                  }}
                  options={{
                    zoomControl: true,
                    streetViewControl: true,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <Marker
                    position={formik.values.location}
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                  />
                  <Circle
                    center={formik.values.location}
                    radius={Number(formik.values.radius)}
                    options={{
                      fillColor: "#1976d2",
                      fillOpacity: 0.2,
                      strokeColor: "#1976d2",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                    }}
                  />
                </GoogleMap>
              </Box>

              <CustomButton
                onClick={() => setIsFullMapOpen(true)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  backgroundColor: "#006EC2",
                  color: "white",
                  fontSize: "12px",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  zIndex: 1000,
                }}
                label={
                  <>
                    {t("dashboard.viewFullMap")}
                    <Box
                      component="img"
                      src={maparrow}
                      alt="view icon"
                      sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }}
                    />
                  </>
                }
              />
            </Box>

            {/* Address display */}
            <textarea
              readOnly
              value={formik.values.location.address}
              rows={2}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                marginTop: "10px",
                backgroundColor: "#f4f4f4",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </Box>

        {/* Submit Button */}
        <CustomButton
          size="small"
          onClick={formik.handleSubmit}
          label={
            loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: "white" }} />
                {t("taskAddModal.update")}
              </Box>
            ) : (
              t("taskAddModal.update")
            )
          }
        />
      </Box>

      {/* Full Map Modal */}
      {isFullMapOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            backgroundColor: "white",
          }}
        >
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={formik.values.location}
            zoom={15}
            onClick={handleMapClick}
            onLoad={(map) => {
              console.log("🗺️ Full map loaded");
              setupStreetView(map);
            }}
            options={{
              zoomControl: true,
              streetViewControl: true,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            <Marker
              position={formik.values.location}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
            />
            <Circle
              center={formik.values.location}
              radius={Number(formik.values.radius)}
              options={{
                fillColor: "#1976d2",
                fillOpacity: 0.2,
                strokeColor: "#1976d2",
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          </GoogleMap>
        </Box>
      )}

      {/* Permission Modal */}
      <LocationPermissionModal
        open={showPermissionModal}
        onAllow={() => {
          console.log("✅ Location permission allowed");
          setShowPermissionModal(false);
          handleGetCurrentLocation();
        }}
        onCancel={() => {
          console.log("❌ Location permission denied");
          setShowPermissionModal(false);
        }}
      />
    </Drawer>
  );
};

export default EditTask;